import { Analytics } from '@cobliteam/analytics';
import { Haversine } from '@cobliteam/common-types';
import { useMap } from '@cobliteam/hooks';
import { usePermissions } from '@cobliteam/permissions';
import { loadAddressRequest } from '@cobliteam/store';
import type { Marker as LeafletMarker } from 'leaflet';
import { memo, useCallback, useMemo, useRef } from 'react';
import { Marker, Popup } from 'react-leaflet';
import { useDispatch, useSelector } from 'react-redux';
import { type ProcessedStop, selectSelectedSavedRoute } from '../../../../../../store';
import { buildTrackingMapFallbackEventId, replaceCurrentTrackingMapEventId, useTrackingMapEventSelection } from '../../../../../../utils/trackingEventDeepLink';

import { IDLE_THRESHOLD_IN_MILLIS_WARN } from '../../../../..';
import { MapStopPopup } from '../../../MapStopPopup/MapStopPopup';
import StopIcon from '../../MapIcons/StopIcon/StopIcon';
import styles from './styles';

interface StopMarkerProps {
  stop: ProcessedStop;
}

const StopMarker = memo(({ stop }: StopMarkerProps) => {
  const ref = useRef<LeafletMarker>(null);
  const { map } = useMap();
  const dispatch = useDispatch();
  const { isAccessAllowed } = usePermissions();
  const selectedSavedRoute = useSelector(selectSelectedSavedRoute);

  const isAllowedToViewPlacesOfInterest = isAccessAllowed('view:places-of-interest');

  const { index: idx, location } = stop;
  const { lat, lng } = location;
  const isIdle = stop.idleOnInMillis > IDLE_THRESHOLD_IN_MILLIS_WARN;
  const eventId = buildTrackingMapFallbackEventId('stop', idx);

  const hasGeofence = useMemo(() => {
    if (!selectedSavedRoute) return stop.geofences.length > 0;

    for (const activity of selectedSavedRoute.activities) {
      const distance = Haversine.kmDistanceBetweenPoints(
        {
          lat: activity.destination.coordinate.latitude,
          lng: activity.destination.coordinate.longitude,
        },
        location
      );

      if (distance <= 300) return true;
    }

    return false;
  }, [selectedSavedRoute, stop.geofences.length, location]);

  // Stable icon identity prevents react-leaflet from calling marker.setIcon() on every re-render,
  // which would otherwise snap a cluster-expanded marker back to the cluster center and re-bind
  // its popup mid-click, blocking the popup from opening.
  const icon = useMemo(
    () =>
      StopIcon(idx, {
        hasGeofence,
        isIdle,
        isAllowedToViewPlacesOfInterest,
      }),
    [idx, hasGeofence, isIdle, isAllowedToViewPlacesOfInterest]
  );

  const handleStopPopupOpen = useCallback(() => {
    replaceCurrentTrackingMapEventId(eventId);
    Analytics.track('Tracking Stop Selected');
    dispatch(loadAddressRequest({ latitude: lat, longitude: lng }));
    if (ref.current && map) {
      map.setView(ref.current.getLatLng(), map.getZoom(), { duration: 0.5 });
    }
  }, [eventId, map, lat, lng, dispatch]);

  const eventHandlers = useMemo(() => ({ click: handleStopPopupOpen }), [handleStopPopupOpen]);

  // Stable position identity. react-leaflet calls marker.setLatLng() on every position
  // prop change; a fresh array literal each render snaps a cluster-expanded marker back
  // to its stored _latlng (cluster center), collapsing the spider expansion and
  // orphaning the popup that Leaflet just opened from the click.
  const position = useMemo<[number, number]>(() => [lat, lng], [lat, lng]);

  const handleMarkerFocus = useCallback(() => {
    if (!map) return;
    if (ref.current?.isPopupOpen()) return;

    // Defer map movement and the synthetic click so Leaflet finishes the current selection cycle before opening the popup.
    setTimeout(() => {
      map.fitBounds(
        [
          [lat, lng],
          [lat, lng],
        ],
        {
          paddingBottomRight: [440, 0],
          maxZoom: 18,
        }
      );

      ref.current?.fire('click');
    }, 300);

    handleStopPopupOpen();
  }, [handleStopPopupOpen, lat, lng, map]);

  useTrackingMapEventSelection(eventId, handleMarkerFocus);

  if (!(Number.isFinite(lat) && Number.isFinite(lng))) return null;

  return (
    <Marker ref={ref} position={position} icon={icon} eventHandlers={eventHandlers}>
      <Popup className={styles.popup} offset={[0, -28]} closeButton={false} maxWidth={undefined} autoPan={false}>
        <MapStopPopup stop={stop} idleTimeHighlighted={isIdle} />
      </Popup>
    </Marker>
  );
});

StopMarker.displayName = 'StopMarker';

export default StopMarker;
